import React from 'react';
import { captureAttributionFromWindow, getAttribution } from './marketingAttribution';
import { notePostludeVisit } from './postludeVisit';

type ChatMessage = { role: 'user' | 'assistant'; text: string };

type GatewayChatResponse = {
  reply?: string;
};

const inferGatewayBaseUrl = (): string => {
  const configured = (process.env.REACT_APP_GATEWAY_URL || '').trim();
  if (configured) {
    if (/^https?:\/\/localhost(?::|\/|$)/i.test(configured)) {
      return configured.replace(/localhost/i, '127.0.0.1').replace(/\/$/, '');
    }
    return configured.replace(/\/$/, '');
  }
  if (typeof window === 'undefined') {
    return 'http://127.0.0.1:8000';
  }
  const host = window.location.hostname;
  const isLocal = host === 'localhost' || host === '127.0.0.1' || host === '::1';
  if (isLocal) {
    return 'http://127.0.0.1:8000';
  }
  const apexHost = host.replace(/^www\./, '').replace(/^dashboard\./, '').replace(/^metrics\./, '');
  return `https://${apexHost}`;
};

const GATEWAY_BASE_URL = inferGatewayBaseUrl();
const GATEWAY_CHAT_URL = `${GATEWAY_BASE_URL}/chat`;
const ASSISTANT_LEAD_URL = `${GATEWAY_BASE_URL}/api/inlet/assistant-leads`;

const EMAIL_RE = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i;
const HIGH_INTENT_PATH = /\/(pricing|demo|products|contact|sales)/i;
const INTENT_WORDS = /\b(demo|trial|pricing|price|buy|talk to sales|walkthrough)\b/i;

const PROMPT_INJECTION_PATTERNS = [
  /ignore\s+(all|previous|prior)\s+instructions/i,
  /reveal\s+(the\s+)?system\s+prompt/i,
  /jailbreak/i,
];

const sanitizePrompt = (raw: string, maxLength = 1200): string =>
  Array.from(raw)
    .map((character) => {
      const code = character.charCodeAt(0);
      return code < 32 || code === 127 ? ' ' : character;
    })
    .join('')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, maxLength);

const isHighIntentPath = (): boolean => {
  if (typeof window === 'undefined') return false;
  return HIGH_INTENT_PATH.test(window.location.pathname);
};

const ACCOUNT_INVITE_DISMISS_KEY = 'nextcompli.accountInvite.dismissed';

const accountInviteCopy = (): { message: string; action: string; dismiss: string } => {
  const spanish = (window.localStorage.getItem('nextcompli.website.language') || '').toLowerCase().startsWith('es');
  if (spanish) {
    return {
      message: 'Hola. Puede abrir una cuenta con su correo. No se necesita una tarjeta.',
      action: 'Crear cuenta',
      dismiss: 'Cerrar la invitación',
    };
  }
  return {
    message: 'Hi. You can open an account with your email. No card is required.',
    action: 'Create account',
    dismiss: 'Dismiss account invite',
  };
};

const shouldOfferAccount = (): boolean => {
  if (typeof window === 'undefined') return false;
  if (window.localStorage.getItem('nextcompli.auth.token')) return false;
  if (window.localStorage.getItem('nextcompli.authenticated') === 'true') return false;
  try {
    if (window.sessionStorage.getItem(ACCOUNT_INVITE_DISMISS_KEY) === '1') return false;
  } catch {
    return false;
  }
  const path = window.location.pathname;
  return !/\/(login|signup|subscribe|console|accept-invite)(\/|$)/.test(path);
};

const accountCreateHref = (): string => {
  const root = window.location.pathname.startsWith('/website') ? '/website' : '';
  return `${root}/subscribe?plan=basic`;
};

const NextCompliAssistant: React.FC = () => {
  const [open, setOpen] = React.useState(false);
  const [accountInvite, setAccountInvite] = React.useState(false);
  const [input, setInput] = React.useState('');
  const [messages, setMessages] = React.useState<ChatMessage[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [email, setEmail] = React.useState('');
  const [company, setCompany] = React.useState('');
  const [fullName, setFullName] = React.useState('');
  const [intent, setIntent] = React.useState<'demo' | 'trial' | 'question'>('demo');
  const [captured, setCaptured] = React.useState(false);
  const [captureError, setCaptureError] = React.useState<string | null>(null);
  const sessionId = React.useRef(`visitor-${Math.random().toString(36).slice(2)}`);
  const chatBodyRef = React.useRef<HTMLDivElement | null>(null);
  const promptedCapture = React.useRef(false);
  const userTurns = messages.filter((row) => row.role === 'user').length;

  React.useEffect(() => {
    captureAttributionFromWindow();
  }, []);

  React.useEffect(() => {
    if (!shouldOfferAccount()) return undefined;
    const timer = window.setTimeout(() => setAccountInvite(true), 1200);
    return () => window.clearTimeout(timer);
  }, []);

  const dismissAccountInvite = () => {
    setAccountInvite(false);
    try {
      window.sessionStorage.setItem(ACCOUNT_INVITE_DISMISS_KEY, '1');
    } catch {
      // A private browser can refuse session storage. The invite still closes.
    }
  };

  React.useEffect(() => {
    const body = chatBodyRef.current;
    if (open && body) {
      body.scrollTop = body.scrollHeight;
    }
  }, [messages, open]);

  const showCapture =
    open &&
    !captured &&
    (userTurns >= 2 || messages.some((row) => INTENT_WORDS.test(row.text)) || (isHighIntentPath() && userTurns >= 1));

  React.useEffect(() => {
    if (!showCapture || promptedCapture.current || captured) return;
    promptedCapture.current = true;
    setMessages((prev) => [
      ...prev,
      {
        role: 'assistant',
        text: isHighIntentPath()
          ? 'If you want a CompliCore trial or a CompliAdvantage walkthrough, leave a work email and company. An AE follows up — we will not book you from this first message.'
          : 'If you want a person to follow up, leave a work email and company. Newsletter signups stay on the email list until you ask for a trial or demo.',
      },
    ]);
  }, [showCapture, captured]);

  const send = async () => {
    const text = sanitizePrompt(input);
    if (!text || loading) return;
    setInput('');
    const foundEmail = text.match(EMAIL_RE);
    if (foundEmail && !email) setEmail(foundEmail[0]);
    setMessages((prev) => [...prev, { role: 'user', text }]);
    if (PROMPT_INJECTION_PATTERNS.some((pattern) => pattern.test(text))) {
      notePostludeVisit('chat', { note: 'unanswered: blocked request' });
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          text: 'I can help with product, implementation, and compliance workflows, but I cannot assist with bypassing safety rules.',
        },
      ]);
      return;
    }
    setLoading(true);
    try {
      const resp = await fetch(GATEWAY_CHAT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt: text,
          session_id: sessionId.current,
          page_path: typeof window === 'undefined' ? '/' : window.location.pathname,
          site: 'nextcompli',
          route_intake: true,
        }),
      });
      const data = resp.ok ? ((await resp.json()) as GatewayChatResponse) : null;
      const reply = data?.reply || 'Sorry, I was unable to get a response. Please try again.';
      const unanswered = /unable to get a response|something went wrong|please try again/i.test(reply);
      notePostludeVisit('chat', { note: unanswered ? `unanswered: ${text}` : text });
      setMessages((prev) => [...prev, { role: 'assistant', text: reply }]);
    } catch {
      notePostludeVisit('chat', { note: `unanswered: ${text}` });
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', text: 'Something went wrong. Please try again, or use the demo form if you need a person.' },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const submitCapture = async (event: React.FormEvent) => {
    event.preventDefault();
    const workEmail = email.trim();
    if (!workEmail || captured) return;
    setCaptureError(null);
    const attribution = getAttribution();
    try {
      const response = await fetch(ASSISTANT_LEAD_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          workEmail,
          fullName: fullName.trim() || 'Website visitor',
          company: company.trim() || 'Unknown company',
          intent,
          session_id: sessionId.current,
          page_path: typeof window === 'undefined' ? '/' : window.location.pathname,
          locale: typeof navigator === 'undefined' ? 'en' : navigator.language,
          notes: messages
            .slice(-6)
            .map((row) => `${row.role}: ${row.text}`)
            .join(' | ')
            .slice(0, 1800),
          ...attribution,
        }),
      });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      setCaptured(true);
      const followUp =
        intent === 'trial'
          ? 'Thanks — we logged a CompliCore trial request. Someone will send next steps. Advantage and Enterprise stay on a guided demo.'
          : 'Thanks — we logged this as a sales lead. An AE will follow up. If we are offline, that still happens from this capture.';
      setMessages((prev) => [...prev, { role: 'assistant', text: followUp }]);
    } catch {
      setCaptureError('Could not save those details. Try the demo form, or send again in a moment.');
    }
  };

  const handleKey = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      void send();
    }
  };

  const invite = accountInviteCopy();

  return (
    <div className="nc-chat-widget" aria-live="polite">
      {accountInvite && !open ? (
        <aside className="nc-account-invite" aria-label={invite.action}>
          <button type="button" className="nc-account-invite-dismiss" onClick={dismissAccountInvite} aria-label={invite.dismiss}>
            ✕
          </button>
          <p>{invite.message}</p>
          <a
            className="nc-account-invite-action"
            href={accountCreateHref()}
            onClick={() => {
              try {
                window.sessionStorage.setItem(ACCOUNT_INVITE_DISMISS_KEY, '1');
              } catch {
                // Navigation still continues when session storage is blocked.
              }
            }}
          >
            {invite.action}
          </a>
        </aside>
      ) : null}
      {open && (
        <div className="nc-chat-panel" role="dialog" aria-label="NextCompli Assistant">
          <div className="nc-chat-header">
            <span>NextCompli Assistant</span>
            <button className="nc-chat-close" onClick={() => setOpen(false)} aria-label="Close chat">
              ✕
            </button>
          </div>
          <div className="nc-chat-body" ref={chatBodyRef}>
            {messages.length === 0 && (
              <p className="nc-chat-empty">
                Hi — ask about screening, pricing, or implementation. I will not push a demo on this first screen.
              </p>
            )}
            {messages.map((message, index) => (
              <div key={`${message.role}-${index}`} className={`nc-chat-msg nc-chat-msg--${message.role}`}>
                <span>{message.text}</span>
              </div>
            ))}
            {loading && <div className="nc-chat-msg nc-chat-msg--assistant nc-chat-typing">Thinking…</div>}
            {showCapture && !captured ? (
              <form className="nc-chat-capture" onSubmit={(event) => void submitCapture(event)}>
                <p className="nc-chat-capture-label">Leave a work email if you want a person to follow up.</p>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="Work email"
                  aria-label="Work email"
                />
                <input
                  value={company}
                  onChange={(event) => setCompany(event.target.value)}
                  placeholder="Company"
                  aria-label="Company"
                />
                <input
                  value={fullName}
                  onChange={(event) => setFullName(event.target.value)}
                  placeholder="Name (optional)"
                  aria-label="Name"
                />
                <select
                  aria-label="What you want"
                  value={intent}
                  onChange={(event) => setIntent(event.target.value as 'demo' | 'trial' | 'question')}
                >
                  <option value="demo">CompliAdvantage / Enterprise demo</option>
                  <option value="trial">CompliCore trial</option>
                  <option value="question">Just a question</option>
                </select>
                {captureError ? <p className="nc-chat-capture-error">{captureError}</p> : null}
                <button type="submit" className="nc-chat-send btn btn-primary">
                  Send my details
                </button>
              </form>
            ) : null}
          </div>
          <div className="nc-chat-footer">
            <textarea
              className="nc-chat-input"
              placeholder="Ask a question…"
              value={input}
              onChange={(event) => setInput(event.target.value)}
              onKeyDown={handleKey}
              rows={2}
              aria-label="Chat message"
            />
            <button className="nc-chat-send btn btn-primary" onClick={() => void send()} disabled={loading || !input.trim()}>
              Send
            </button>
          </div>
        </div>
      )}
      <button
        className={`nc-chat-fab ${open ? 'nc-chat-fab--open' : ''}`}
        onClick={() => {
          setAccountInvite(false);
          setOpen((value) => !value);
        }}
        aria-label={open ? 'Close chat' : 'Open chat with NextCompli Assistant'}
      >
        {open ? '✕' : '💬'}
      </button>
    </div>
  );
};

export default NextCompliAssistant;
